import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Sparkles, LogOut, Home, BookOpen, Download, RotateCcw, Save,
  Activity, Moon, Flame, Brain, Heart, Zap, ChevronRight, Shield
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'hikari-body-compass-data-v2';

const metrics = [
  { key: 'energy', label: 'Energy', icon: Zap, goodHigh: true, default: 6 },
  { key: 'sleep', label: 'Sleep quality', icon: Moon, goodHigh: true, default: 7 },
  { key: 'digestion', label: 'Digestion', icon: Activity, goodHigh: true, default: 6 },
  { key: 'inflammation', label: 'Inflammation / pain', icon: Flame, goodHigh: false, default: 4 },
  { key: 'clarity', label: 'Mental clarity', icon: Brain, goodHigh: true, default: 5 },
  { key: 'nervous', label: 'Nervous system ease', icon: Heart, goodHigh: true, default: 4 }
];

const symptomCategories = {
  head: ['Headaches', 'Dizziness', 'Faintness', 'Insomnia'],
  eyes: ['Watery eyes', 'Dark circles', 'Blurred vision', 'Itchy eyes'],
  ears: ['Ear ringing', 'Ear pain', 'Hearing changes'],
  nose: ['Stuffy nose', 'Sinus pressure', 'Sneezing', 'Post-nasal drip'],
  throat: ['Sore throat', 'Throat clearing', 'Chronic cough', 'Hoarseness'],
  skin: ['Acne', 'Rashes', 'Hair loss', 'Hot flashes', 'Excessive sweating', 'Dry skin'],
  heart: ['Palpitations', 'Rapid heartbeat', 'Chest tightness'],
  lungs: ['Shortness of breath', 'Chest congestion', 'Wheezing'],
  digestive: ['Bloating', 'Nausea', 'Constipation', 'Diarrhea', 'Heartburn', 'Gas', 'Stomach pain'],
  musculoskeletal: ['Joint pain', 'Muscle aches', 'Stiffness', 'Neck tension', 'Back pain', 'Weakness'],
  weight: ['Food cravings', 'Binge eating', 'Water retention', 'Appetite changes'],
  energy: ['Fatigue', 'Energy crashes', 'Restlessness', 'Afternoon slump'],
  mind: ['Brain fog', 'Poor memory', 'Poor concentration', 'Difficulty deciding'],
  emotions: ['Mood swings', 'Anxiety', 'Irritability', 'Depression', 'Nervousness'],
  other: ['Cold hands', 'Tingling', 'Chemical sensitivity', 'Histamine flares', 'Heavy cycles', 'Frequent illness', 'Restless sleep']
};

const allSymptoms = Object.values(symptomCategories).flat();

const pathwayDefinitions = {
  thyroid: {
    label: 'Thyroid / metabolic',
    color: 'mint',
    tests: ['TSH', 'Free T4', 'Free T3', 'Thyroid antibodies (TPO, TgAb)', 'Reverse T3'],
    why: 'Often worth exploring when fatigue, coldness, low energy, weight changes, or sluggishness cluster together.'
  },
  nutrient: {
    label: 'Nutrient status',
    color: 'violet',
    tests: ['B12', 'Folate', 'Ferritin', 'Iron panel', 'Vitamin D', 'Zinc', 'Magnesium RBC', 'Omega-3 index'],
    why: 'Useful when brain fog, tingling, hair changes, low stamina, or cognitive issues repeat.'
  },
  inflammation: {
    label: 'Inflammatory markers',
    color: 'gold',
    tests: ['hs-CRP', 'ESR', 'Homocysteine', 'Fibrinogen', 'Ferritin'],
    why: 'Helpful when pain, flares, immune noise, or post-exertional heaviness keep surfacing.'
  },
  histamine: {
    label: 'Histamine / MCAS',
    color: 'pink',
    tests: ['DAO enzyme', 'Histamine plasma', 'Tryptase', 'Total IgE', 'Prostaglandin D2'],
    why: 'Useful when allergy-like flares, chemical sensitivity, flushing, or hives appear.'
  },
  glucose: {
    label: 'Glucose / metabolic',
    color: 'blue',
    tests: ['Fasting glucose', 'HbA1c', 'Fasting insulin', 'HOMA-IR', 'Leptin', 'Uric acid'],
    why: 'Worth exploring when energy crashes, shakiness, cravings, or weight issues appear.'
  },
  stress: {
    label: 'Stress / HPA axis',
    color: 'violet',
    tests: ['Cortisol (4-point)', 'DHEA-S', 'Pregnenolone', 'Melatonin'],
    why: 'Relevant when sleep is fragile, tension is high, and recovery lags behind effort.'
  },
  hormonal: {
    label: 'Hormonal balance',
    color: 'pink',
    tests: ['Estradiol', 'Progesterone', 'Testosterone (free/total)', 'DHEA-S', 'SHBG', 'FSH', 'LH', 'Prolactin'],
    why: 'Worth exploring when cycles are irregular, hot flashes occur, or mood shifts with hormonal timing.'
  },
  gut: {
    label: 'Gut / microbiome',
    color: 'gold',
    tests: ['GI-MAP', 'SIBO breath test', 'Zonulin', 'Calprotectin', 'Secretory IgA', 'Pancreatic elastase'],
    why: 'Helpful when bloating, irregular bowel patterns, or food reactions persist.'
  },
  immune: {
    label: 'Immune regulation',
    color: 'mint',
    tests: ['IgA', 'IgG', 'IgM', 'WBC differential', 'NK cells', 'Vitamin D'],
    why: 'Useful when frequent illness, slow recovery, or immune dysregulation appears.'
  },
  cardiovascular: {
    label: 'Cardiovascular',
    color: 'blue',
    tests: ['ApoB', 'Lp(a)', 'LDL particle number', 'HDL', 'Triglycerides', 'Homocysteine', 'hs-CRP'],
    why: 'Worth exploring when palpitations, chest discomfort, or cardiovascular risk factors present.'
  },
  liver: {
    label: 'Liver / detox',
    color: 'gold',
    tests: ['ALT', 'AST', 'GGT', 'Alkaline phosphatase', 'Bilirubin', 'Albumin'],
    why: 'Helpful when fatigue, chemical sensitivity, skin issues, or medication tolerance is poor.'
  },
  kidney: {
    label: 'Kidney function',
    color: 'blue',
    tests: ['Creatinine', 'BUN', 'eGFR', 'Cystatin C', 'Microalbumin/creatinine ratio'],
    why: 'Worth checking when blood pressure, swelling, or urinary changes appear.'
  },
  blood: {
    label: 'Blood / anemia',
    color: 'pink',
    tests: ['CBC', 'RBC indices (MCV, MCH, MCHC)', 'Reticulocytes', 'Ferritin', 'B12', 'Folate'],
    why: 'Useful when fatigue, pallor, shortness of breath, or cold intolerance persist.'
  },
  autoimmune: {
    label: 'Autoimmunity',
    color: 'violet',
    tests: ['ANA', 'ANA pattern/titer', 'Rheumatoid factor', 'Anti-CCP', 'Anti-dsDNA', 'Complement C3/C4'],
    why: 'Helpful when joint pain, rashes, fatigue, or multi-system symptoms appear without clear cause.'
  },
  toxins: {
    label: 'Environmental toxins',
    color: 'gold',
    tests: ['Lead', 'Mercury', 'Arsenic', 'Cadmium', 'Mycotoxins panel', 'Glyphosate'],
    why: 'Worth exploring when neurological symptoms, fatigue, or unexplained illness persists.'
  },
  electrolytes: {
    label: 'Electrolytes / minerals',
    color: 'mint',
    tests: ['Sodium', 'Potassium', 'Chloride', 'CO2', 'Calcium', 'Magnesium', 'Phosphorus'],
    why: 'Useful when muscle cramps, fatigue, heart rhythm issues, or hydration problems occur.'
  },
  bone: {
    label: 'Bone health',
    color: 'violet',
    tests: ['Vitamin D', 'Calcium', 'PTH', 'Osteocalcin', 'CTX', 'P1NP'],
    why: 'Helpful when fracture risk, bone pain, or osteoporosis concerns exist.'
  },
  neurological: {
    label: 'Brain / neurological',
    color: 'blue',
    tests: ['Homocysteine', 'B12', 'Folate', 'Omega-3 index', 'hs-CRP', 'HbA1c', 'ApoE genotype'],
    why: 'Worth exploring when cognitive decline, brain fog, or neurological symptoms appear.'
  }
};

function seedHistory() {
  const today = new Date();
  const rows = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    rows.push({
      date: d.toISOString().slice(0, 10),
      energy: 4 + ((i * 2) % 4),
      sleep: 5 + (i % 3),
      inflammation: 3 + (i % 4),
      clarity: 4 + ((i + 1) % 3)
    });
  }
  return rows;
}

const defaultState = () => ({
  date: new Date().toISOString().slice(0, 10),
  metrics: Object.fromEntries(metrics.map(m => [m.key, m.default])),
  symptoms: ['Cold hands', 'Brain fog'],
  supplements: 'NAC\nNMN\nD3 + K2\nMagnesium',
  notes: '',
  history: seedHistory()
});

export default function TrackerPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultState(), ...JSON.parse(saved) } : defaultState();
    } catch {
      return defaultState();
    }
  });

  const saveState = useCallback((newState) => {
    setState(newState);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  }, []);

  const handleMetricChange = (key, value) => {
    const newState = {
      ...state,
      metrics: { ...state.metrics, [key]: Number(value) }
    };
    saveState(newState);
  };

  const toggleSymptom = (symptom) => {
    const newSymptoms = state.symptoms.includes(symptom)
      ? state.symptoms.filter(s => s !== symptom)
      : [...state.symptoms, symptom];
    saveState({ ...state, symptoms: newSymptoms });
  };

  const computePathways = () => {
    const w = { 
      thyroid: 0, nutrient: 0, inflammation: 0, histamine: 0, glucose: 0, stress: 0, 
      hormonal: 0, gut: 0, immune: 0, cardiovascular: 0, liver: 0, kidney: 0, 
      blood: 0, autoimmune: 0, toxins: 0, electrolytes: 0, bone: 0, neurological: 0 
    };
    const m = state.metrics;
    
    // Metric-based weights
    if (m.energy <= 4) { w.thyroid += 3; w.nutrient += 3; w.glucose += 2; w.blood += 2; }
    if (m.sleep <= 4) { w.stress += 3; w.glucose += 1; w.hormonal += 1; w.neurological += 1; }
    if (m.digestion <= 4) { w.gut += 4; w.inflammation += 2; w.histamine += 1; w.liver += 1; }
    if (m.inflammation >= 6) { w.inflammation += 4; w.histamine += 2; w.immune += 2; w.autoimmune += 2; }
    if (m.clarity <= 4) { w.nutrient += 3; w.glucose += 1; w.thyroid += 1; w.neurological += 3; }
    if (m.nervous <= 4) { w.stress += 4; w.histamine += 1; w.cardiovascular += 1; w.electrolytes += 1; }

    const symptoms = new Set(state.symptoms);
    
    // Thyroid
    ['Cold hands', 'Fatigue', 'Hair loss', 'Dry skin', 'Constipation', 'Appetite changes'].forEach(s => {
      if (symptoms.has(s)) w.thyroid += 3;
    });
    
    // Nutrient status
    ['Brain fog', 'Tingling', 'Poor memory', 'Poor concentration', 'Weakness', 'Dizziness', 'Hair loss'].forEach(s => {
      if (symptoms.has(s)) w.nutrient += 3;
    });
    
    // Inflammation
    ['Joint pain', 'Muscle aches', 'Stiffness', 'Rashes', 'Acne', 'Back pain'].forEach(s => {
      if (symptoms.has(s)) w.inflammation += 3;
    });
    
    // Histamine / MCAS
    ['Chemical sensitivity', 'Histamine flares', 'Watery eyes', 'Stuffy nose', 'Sneezing', 'Itchy eyes', 'Rashes'].forEach(s => {
      if (symptoms.has(s)) w.histamine += 3;
    });
    
    // Glucose / metabolic
    ['Energy crashes', 'Food cravings', 'Afternoon slump', 'Binge eating', 'Irritability'].forEach(s => {
      if (symptoms.has(s)) w.glucose += 3;
    });
    
    // Stress / HPA axis
    ['Insomnia', 'Restless sleep', 'Neck tension', 'Anxiety', 'Nervousness'].forEach(s => {
      if (symptoms.has(s)) w.stress += 3;
    });
    
    // Hormonal balance
    ['Hot flashes', 'Heavy cycles', 'Mood swings', 'Hair loss', 'Acne', 'Water retention'].forEach(s => {
      if (symptoms.has(s)) w.hormonal += 3;
    });
    
    // Gut / microbiome
    ['Bloating', 'Gas', 'Constipation', 'Diarrhea', 'Heartburn', 'Nausea', 'Stomach pain'].forEach(s => {
      if (symptoms.has(s)) w.gut += 3;
    });
    
    // Immune regulation
    ['Frequent illness', 'Sinus pressure', 'Sore throat', 'Chronic cough', 'Chest congestion', 'Wheezing'].forEach(s => {
      if (symptoms.has(s)) w.immune += 3;
    });
    
    // Cardiovascular
    ['Palpitations', 'Rapid heartbeat', 'Chest tightness', 'Dizziness', 'Faintness', 'Shortness of breath'].forEach(s => {
      if (symptoms.has(s)) w.cardiovascular += 3;
    });
    
    // Liver / detox
    ['Chemical sensitivity', 'Nausea', 'Fatigue', 'Rashes', 'Acne', 'Headaches'].forEach(s => {
      if (symptoms.has(s)) w.liver += 2;
    });
    
    // Kidney function
    ['Water retention', 'Fatigue', 'Back pain', 'Frequent illness'].forEach(s => {
      if (symptoms.has(s)) w.kidney += 2;
    });
    
    // Blood / anemia
    ['Fatigue', 'Dizziness', 'Shortness of breath', 'Weakness', 'Cold hands', 'Hair loss'].forEach(s => {
      if (symptoms.has(s)) w.blood += 2;
    });
    
    // Autoimmunity
    ['Joint pain', 'Rashes', 'Fatigue', 'Hair loss', 'Stiffness', 'Dry skin'].forEach(s => {
      if (symptoms.has(s)) w.autoimmune += 2;
    });
    
    // Environmental toxins
    ['Chemical sensitivity', 'Brain fog', 'Fatigue', 'Headaches', 'Tingling', 'Muscle aches'].forEach(s => {
      if (symptoms.has(s)) w.toxins += 2;
    });
    
    // Electrolytes / minerals
    ['Muscle aches', 'Weakness', 'Palpitations', 'Dizziness', 'Fatigue'].forEach(s => {
      if (symptoms.has(s)) w.electrolytes += 2;
    });
    
    // Bone health
    ['Joint pain', 'Back pain', 'Stiffness', 'Weakness'].forEach(s => {
      if (symptoms.has(s)) w.bone += 2;
    });
    
    // Brain / neurological
    ['Brain fog', 'Poor memory', 'Poor concentration', 'Headaches', 'Dizziness', 'Tingling', 'Mood swings'].forEach(s => {
      if (symptoms.has(s)) w.neurological += 3;
    });

    return Object.entries(w)
      .map(([key, value]) => ({ key, value, ...pathwayDefinitions[key] }))
      .sort((a, b) => b.value - a.value);
  };

  const pathways = computePathways();
  const maxPathway = Math.max(10, ...pathways.map(p => p.value));

  const saveToday = () => {
    const today = {
      date: state.date,
      energy: state.metrics.energy,
      sleep: state.metrics.sleep,
      inflammation: state.metrics.inflammation,
      clarity: state.metrics.clarity
    };
    const history = [...state.history.filter(row => row.date !== state.date), today].slice(-14);
    saveState({ ...state, history });
  };

  const exportSnapshot = () => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hikari-body-compass-${state.date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetData = () => {
    if (window.confirm('This will reset all your local data. Are you sure?')) {
      localStorage.removeItem(STORAGE_KEY);
      setState(defaultState());
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const chartData = state.history.slice(-7).map(row => ({
    ...row,
    dateLabel: row.date.slice(5)
  }));

  const getColorClass = (color) => {
    const colors = {
      mint: 'text-[#2DD4BF]',
      violet: 'text-[#8B5CF6]',
      gold: 'text-[#FBBF24]',
      blue: 'text-[#3B82F6]',
      pink: 'text-[#F472B6]'
    };
    return colors[color] || 'text-white';
  };

  const getBarClass = (color) => {
    const colors = {
      mint: 'progress-bar-mint',
      violet: 'progress-bar-violet',
      gold: 'progress-bar-gold',
      blue: 'progress-bar-blue',
      pink: 'progress-bar-pink'
    };
    return colors[color] || 'progress-bar-mint';
  };

  return (
    <div className="bg-hikari min-h-screen">
      <div className="orb orb-mint"></div>
      <div className="orb orb-violet"></div>

      <div className="content-wrapper">
        {/* Header */}
        <header className="glass mx-4 md:mx-6 mt-4 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#0A0B10]" />
            </div>
            <div>
              <div className="kicker text-[#2DD4BF] text-[10px]">Hikari Health</div>
              <div className="text-sm font-semibold">Functional Tracker</div>
            </div>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link to="/" className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="nav-home">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/tutor" className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="nav-tutor">
              <BookOpen className="w-5 h-5" />
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="p-2 rounded-lg hover:bg-white/5 text-[#FBBF24] hover:text-[#FCD34D]" data-testid="nav-admin">
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="kicker text-[#FBBF24] mb-2">Interactive prototype</div>
              <h1 className="font-display text-3xl md:text-4xl font-light">Daily signal map</h1>
              <p className="text-[#94A3B8] mt-2 text-sm">
                Welcome, {user?.name}. Your data stays on this device.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={resetData} className="btn-secondary py-2 px-4 text-sm flex items-center gap-2" data-testid="reset-button">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
              <button onClick={exportSnapshot} className="btn-secondary py-2 px-4 text-sm flex items-center gap-2" data-testid="export-button">
                <Download className="w-4 h-4" /> Export
              </button>
              <button onClick={saveToday} className="btn-primary py-2 px-4 text-sm flex items-center gap-2" data-testid="save-button">
                <Save className="w-4 h-4" /> Save today
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Body State Sliders */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="kicker text-[#94A3B8] mb-1">Daily scores</div>
                    <h2 className="text-lg font-semibold">Body state sliders</h2>
                  </div>
                  <span className="text-xs text-[#475569]">Local-only entry</span>
                </div>
                
                <div className="space-y-5">
                  {metrics.map(metric => (
                    <div key={metric.key} className="flex items-center gap-4">
                      <div className="flex items-center gap-2 w-40 flex-shrink-0">
                        <metric.icon className="w-4 h-4 text-[#94A3B8]" />
                        <span className="text-sm text-[#94A3B8]">{metric.label}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={state.metrics[metric.key]}
                        onChange={(e) => handleMetricChange(metric.key, e.target.value)}
                        className="flex-1"
                        data-testid={`slider-${metric.key}`}
                      />
                      <span className="w-12 text-right text-sm font-medium">
                        {state.metrics[metric.key]}/10
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Symptom Tags */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="kicker text-[#94A3B8] mb-1">Signal clusters</div>
                    <h2 className="text-lg font-semibold">What's showing up today</h2>
                  </div>
                  <span className="text-xs text-[#475569]">{state.symptoms.length} selected</span>
                </div>
                
                <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                  {Object.entries(symptomCategories).map(([category, symptoms]) => (
                    <div key={category}>
                      <div className="text-xs text-[#64748B] uppercase tracking-wider mb-2 capitalize">{category}</div>
                      <div className="flex flex-wrap gap-2">
                        {symptoms.map(symptom => (
                          <button
                            key={symptom}
                            onClick={() => toggleSymptom(symptom)}
                            className={`chip ${state.symptoms.includes(symptom) ? 'active' : ''}`}
                            data-testid={`symptom-${symptom.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {symptom}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trend Chart */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="kicker text-[#94A3B8] mb-1">Pattern layer</div>
                    <h2 className="text-lg font-semibold">7-day signal view</h2>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#2DD4BF]"></span> Energy</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#3B82F6]"></span> Sleep</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FBBF24]"></span> Inflammation</span>
                  </div>
                </div>
                
                <div className="h-64" data-testid="trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <XAxis 
                        dataKey="dateLabel" 
                        stroke="#475569" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis 
                        domain={[0, 10]} 
                        stroke="#475569" 
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'rgba(18, 20, 29, 0.9)', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '12px',
                          color: '#F8FAFC'
                        }}
                      />
                      <Line type="monotone" dataKey="energy" stroke="#2DD4BF" strokeWidth={3} dot={{ fill: '#2DD4BF', r: 4 }} />
                      <Line type="monotone" dataKey="sleep" stroke="#3B82F6" strokeWidth={3} dot={{ fill: '#3B82F6', r: 4 }} />
                      <Line type="monotone" dataKey="inflammation" stroke="#FBBF24" strokeWidth={3} dot={{ fill: '#FBBF24', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Lab Suggestions */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="kicker text-[#94A3B8] mb-1">Suggested next look</div>
                    <h2 className="text-lg font-semibold">Exploratory labs</h2>
                  </div>
                  <span className="text-xs text-[#475569]">Educational, not diagnostic</span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {pathways.slice(0, 4).map(pathway => (
                    <div key={pathway.key} className="glass-subtle p-5">
                      <span className={`pathway-pill ${pathway.color} mb-3 inline-block`}>
                        {pathway.label}
                      </span>
                      <div className="font-semibold mb-2">{pathway.tests.join(' • ')}</div>
                      <p className="text-sm text-[#94A3B8]">{pathway.why}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Pathway Weights */}
              <div className="glass-card p-6">
                <div className="mb-6">
                  <div className="kicker text-[#94A3B8] mb-1">Body-system map</div>
                  <h2 className="text-lg font-semibold">Weighted pathways</h2>
                </div>
                
                <div className="space-y-4">
                  {pathways.map(pathway => (
                    <div key={pathway.key}>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className={getColorClass(pathway.color)}>{pathway.label}</span>
                        <span className="text-[#475569]">{pathway.value} pts</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className={`h-full rounded-full ${getBarClass(pathway.color)}`}
                          style={{ width: `${(pathway.value / maxPathway) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supplements */}
              <div className="glass-card p-6">
                <div className="mb-4">
                  <div className="kicker text-[#94A3B8] mb-1">Supplement view</div>
                  <h2 className="text-lg font-semibold">Today's stack</h2>
                </div>
                <textarea
                  value={state.supplements}
                  onChange={(e) => saveState({ ...state, supplements: e.target.value })}
                  className="w-full h-32 resize-none"
                  placeholder="List your supplements..."
                  data-testid="supplements-textarea"
                />
              </div>

              {/* Notes */}
              <div className="glass-card p-6">
                <div className="mb-4">
                  <div className="kicker text-[#94A3B8] mb-1">Clinical notes</div>
                  <h2 className="text-lg font-semibold">Context</h2>
                </div>
                <textarea
                  value={state.notes}
                  onChange={(e) => saveState({ ...state, notes: e.target.value })}
                  className="w-full h-32 resize-none"
                  placeholder="How did the day feel?..."
                  data-testid="notes-textarea"
                />
              </div>

              {/* AI Tutor CTA */}
              <Link to="/tutor" className="glass-card p-6 block group hover:border-[#2DD4BF]/30 transition-colors" data-testid="tutor-cta">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="kicker text-[#2DD4BF] mb-1">Learn more</div>
                    <h2 className="text-lg font-semibold">Hikari AI Tutor</h2>
                    <p className="text-sm text-[#94A3B8] mt-1">Explore functional medicine concepts</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#2DD4BF] transition-colors" />
                </div>
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
